import type { LoginRequest, RegisterRequest, TokenDto, UserDto } from "@/types/auth";
import type { ApiResponse } from "@/types/common";
import { apiClient, unwrapApiResponse } from "./httpClient";
import { clearTokens, setTokens } from "./tokenStorage";

export interface OtpConfigDto {
  useDemoOtp: boolean;
  otpExpiryMinutes: number;
  demoOtpCode?: string;
}

export const authService = {
  async login(request: LoginRequest): Promise<TokenDto> {
    const token = await unwrapApiResponse(
      apiClient.post<ApiResponse<TokenDto>>("/Auth/login", request),
    );
    setTokens(token.accessToken, token.refreshToken);
    return token;
  },

  async adminLogin(request: LoginRequest): Promise<TokenDto> {
    const token = await unwrapApiResponse(
      apiClient.post<ApiResponse<TokenDto>>("/Auth/admin/login", request),
    );
    setTokens(token.accessToken, token.refreshToken);
    return token;
  },

  register(request: RegisterRequest): Promise<UserDto> {
    return unwrapApiResponse(apiClient.post<ApiResponse<UserDto>>("/Auth/register", request));
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post<ApiResponse>("/Auth/logout");
    } finally {
      clearTokens();
    }
  },

  forgotPassword(email: string): Promise<void> {
    return unwrapApiResponse(apiClient.post<ApiResponse>("/Auth/forgot-password", { email }));
  },

  resetPassword(email: string, token: string, newPassword: string): Promise<void> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse>("/Auth/reset-password", { email, token, newPassword }),
    );
  },

  getOtpConfig(): Promise<OtpConfigDto> {
    return unwrapApiResponse(apiClient.get<ApiResponse<OtpConfigDto>>("/Auth/otp/config"));
  },

  sendLoginOtp(phoneNumber: string): Promise<void> {
    return unwrapApiResponse(apiClient.post<ApiResponse>("/Auth/otp/send", { phoneNumber }));
  },

  async verifyLoginOtp(phoneNumber: string, otp: string): Promise<TokenDto> {
    const token = await unwrapApiResponse(
      apiClient.post<ApiResponse<TokenDto>>("/Auth/otp/verify", { phoneNumber, otp }),
    );
    setTokens(token.accessToken, token.refreshToken);
    return token;
  },

  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse>("/Auth/change-password", {
        currentPassword,
        newPassword,
      }),
    );
  },
};
